/**
 * Globaler Voice-Zustand (zustand). Lebt unabhängig von der geöffneten Ansicht, damit die Statusleiste in der
 * Kanalliste auch dann funktioniert, wenn der Nutzer gerade einen Textkanal liest. Geschrieben wird nur vom
 * VoiceController (controller.ts); Komponenten lesen und rufen Controller-Aktionen auf.
 */
import { DEFAULT_SCREENSHARE_PRESET, type ScreensharePreset, type VoiceJoinResponseDto, type VoiceStateDto } from '@lagerfeuer/shared';
import { create } from 'zustand';
import type { DevicePrefs, QualityLevel, VoiceDeviceKind, VoiceStatus } from './logic';
import type { ParticipantView } from './snapshot';

export interface DeviceOption {
  deviceId: string;
  label: string;
}

/** Rechte/Parameter aus der Join-Antwort, die die UI braucht. */
export type VoiceSession = Pick<
  VoiceJoinResponseDto,
  'canPublishAudio' | 'canPublishVideo' | 'canScreenshare' | 'bitrate' | 'screenshareMaxPreset' | 'userLimit' | 'maxAudioBitrate'
>;

export interface UserInfo {
  name: string;
  avatarUrl: string | null;
}

export interface VoiceStoreState {
  status: VoiceStatus;
  serverId: string | null;
  channelId: string | null;
  session: VoiceSession | null;
  selfMute: boolean;
  selfDeaf: boolean;
  /** Vom Server (Moderation) stumm-/taubgeschaltet – aus dem eigenen VoiceState. */
  serverMute: boolean;
  serverDeaf: boolean;
  camera: boolean;
  screen: boolean;
  /** Läuft gerade eine Medienaktion (Doppelklicks verhindern)? */
  busy: Partial<Record<'mic' | 'camera' | 'screen', boolean>>;
  screenPreset: ScreensharePreset;
  participants: ParticipantView[];
  quality: QualityLevel;
  /** false, wenn der Browser Audio-Wiedergabe blockiert (dann Knopf „Audio aktivieren"). */
  canPlaybackAudio: boolean;
  error: string | null;
  devices: Record<VoiceDeviceKind, DeviceOption[]>;
  activeDevices: DevicePrefs;
  volumes: Record<string, number>;
  /** Kanal → bekannte VoiceStates (REST bzw. Socket). */
  channelStates: Record<string, VoiceStateDto[]>;
  channelNames: Record<string, string>;
  users: Record<string, UserInfo>;
}

export const initialVoiceState = (): VoiceStoreState => ({
  status: 'idle',
  serverId: null,
  channelId: null,
  session: null,
  selfMute: false,
  selfDeaf: false,
  serverMute: false,
  serverDeaf: false,
  camera: false,
  screen: false,
  busy: {},
  screenPreset: DEFAULT_SCREENSHARE_PRESET,
  participants: [],
  quality: 'unknown',
  canPlaybackAudio: true,
  error: null,
  devices: { audioinput: [], videoinput: [], audiooutput: [] },
  activeDevices: {},
  volumes: {},
  channelStates: {},
  channelNames: {},
  users: {},
});

export const useVoiceStore = create<VoiceStoreState>(() => initialVoiceState());

/** Zustände, die beim Verlassen zurückgesetzt werden (Caches/Einstellungen bleiben). */
export function connectionResetPatch(): Partial<VoiceStoreState> {
  const init = initialVoiceState();
  return {
    status: init.status,
    serverId: null,
    channelId: null,
    session: null,
    serverMute: false,
    serverDeaf: false,
    camera: false,
    screen: false,
    busy: {},
    participants: [],
    quality: init.quality,
    canPlaybackAudio: true,
  };
}
